import dayjs from 'dayjs';
import cookie from 'js-cookie';
import type { PAGE_POSITIONS } from 'types';
import type { TrackableProduct } from 'types/TrackableProduct';
import type { User } from 'types/User';
import * as uuid from 'uuid';

import algolia from '~/services/algolia.client';
import braze from '~/services/braze.client';
import facebook from '~/services/facebook';
import facebookPixel from '~/services/facebook.pixel';
import gtm from '~/services/gtm.client';
import tiktokServer from '~/services/tiktok';
import tiktok from '~/services/tiktok.pixel';
import twitter from '~/services/twitter.pixel';
import { sha256 } from '~/utils/hash';
import {
  getEntrypoint,
  getPageType,
  getPreviousPagePath,
  getPreviousPagePosition,
  getPreviousPageType,
  getSourceAndMedium
} from '~/utils/navigationContext';

import UserEvent from './UserEvent';

type EventData = {
  affiliateId: string;
  affiliateName: string;
  buttonPosition: number;
  clientIp: string | null;
  currency: string;
  isBot: boolean;
  isReseller: boolean;
  pagePath: string;
  pagePosition: PAGE_POSITIONS;
  price: number | null;
  product: TrackableProduct;
  productImageUrl?: string;
  productUrl?: string;
  queryID?: string | null;
  user: User;
};

class AffiliateBuyNowEvent extends UserEvent<EventData> {
  async sendToFacebook() {
    const eventId = uuid.v4();

    await facebookPixel.fireEvent({
      data: {
        affiliate: this.getAffiliateName(),
        content_ids: [this.getProductId()],
        content_type: 'product_group',
        currency: this.getCurrencey(),
        value: this.getPrice()
      },
      eventID: eventId,
      eventName: facebookPixel.StandardEventNames.AddToCart
    });

    const hashedProfile = await facebook.getHashedProfile(
      this.data.user.profile
    );

    const externalId = await sha256(this.data.user.id ?? 'guest');

    const serverEventData = [
      {
        action_source: 'website',
        custom_data: {
          brand: this.data.product?.brand || '',
          content_ids: [this.getProductId()],
          content_name: this.data.product?.name ?? '',
          content_type: 'product_group',
          currency: this.data.product?.price.currency ?? '',
          value: this.getPrice()
        },
        event_id: eventId,
        event_name: facebookPixel.StandardEventNames.AddToCart,
        event_source_url: this.data.pagePath,
        event_time: dayjs().unix(),
        user_data: {
          client_ip_address: this.data.clientIp,
          client_user_agent: navigator?.userAgent,
          external_id: externalId,
          fbc: cookie.get('_fbc'),
          fbp: cookie.get('_fbp'),
          ...hashedProfile
        }
      }
    ];

    this.serverEvents.push({ data: serverEventData, type: 'facebook' });
  }

  async sendToTikTok() {
    const eventId = uuid.v4();
    const hashedProfile = await tiktokServer.getHashedProfile(
      this.data.user.profile
    );

    this.serverEvents.push({
      data: [
        {
          context: {
            ip: this.data.clientIp ?? '',
            page: { url: this.data.pagePath },
            user_agent: navigator?.userAgent ?? ''
          },
          event: 'AddToCartAF',
          event_id: eventId,
          event_time: Math.floor(Date.now() / 1000),
          properties: {
            contents: [
              {
                content_id: String(this.getProductId()),
                content_type: 'product'
              }
            ],
            currency: this.getCurrencey(),
            value: this.getPrice()
          },
          user: {
            external_id: this.data.user.id ?? 'guest',
            ttclid: cookie.get('tss_ttclid'),
            ttp: cookie.get('_ttp'),
            ...hashedProfile
          }
        }
      ],
      type: 'tiktok'
    });

    tiktok.fireEvent(
      'AddToCartAF',
      {
        affiliate: this.getAffiliateName(),
        content_id: this.getProductId(),
        content_type: 'product',
        currency: this.getCurrencey(),
        value: this.getPrice()
      },
      eventId
    );
  }

  sendToTwitter() {
    return twitter.fireEvent(twitter.EventIds.AddToCartAF, {
      contents: [{ content_id: this.getProductId(), content_type: 'product' }],
      currency: this.getCurrencey(),
      value: this.getPrice()
    });
  }

  sendToGtm() {
    gtm.pushToDataLayer({
      button_position: this.data.buttonPosition,
      currency: this.getCurrencey(),
      entry_point: getEntrypoint(),
      event: 'retailerLinkClick',
      is_logged_in: this.data.user.isLoggedIn,
      is_reseller: this.data.isReseller,
      page_path: window.location.pathname,
      page_position: this.data.pagePosition,
      page_type: getPageType(),
      platform: 'web',
      position: this.data.buttonPosition,
      previous_page_path: getPreviousPagePath(),
      previous_page_position: getPreviousPagePosition(),
      previous_page_type: getPreviousPageType(),
      price: this.getPrice(),
      product_brand: this.data.product.brand,
      product_id: this.getProductId(),
      product_name: this.getProductName(),
      product_sub_brand: this.data.product.sub_brand,
      product_view_price:
        this.data.product.price.sale ?? this.data.product.price.regular,
      retailer_click_price: this.getPrice(),
      retailer_name: this.getAffiliateName(),
      source_medium: getSourceAndMedium(),
      stock_status: this.data.product.stock_status,
      style_code: this.data.product.style_code,
      user_id: this.data.user.id || null
    });

    gtm.Ecommerce.addToCart({
      affiliateName: this.getAffiliateName(),
      buttonPosition: this.data.buttonPosition,
      currency: this.getCurrencey(),
      isReseller: this.data.isReseller,
      item: gtm.Ecommerce.getEcommerceItem(this.data.product),
      pagePosition: this.data.pagePosition,
      previousPagePosition: getPreviousPagePosition(),
      price: this.getPrice()
    });
  }

  sendToAlgolia() {
    if (!this.data.queryID) return;

    algolia.addedToCartObjectIDsAfterSearch({
      id: this.getProductId()?.toString(),
      queryID: this.data.queryID,
      user: this.data.user
    });
  }

  sendToBraze(): void {
    braze.logAffiliateViewedEvent({
      imageUrl: this.data.productImageUrl,
      productId: this.getProductId(),
      productName: this.data.product?.name ?? '',
      productType: this.data.product?.sub_type,
      productUrl: this.data.productUrl,
      retailerName: this.data.affiliateName
    });
  }

  collectServerEvents() {
    this.serverEvents.push({
      data: {
        button_position: this.data.buttonPosition,
        client_ip_address: this.data.clientIp ?? '',
        currency: this.getCurrencey(),
        entry_point: getEntrypoint(),
        is_logged_in: this.data.user.isLoggedIn,
        is_reseller: this.data.isReseller,
        page_path: window.location.pathname,
        page_position: this.data.pagePosition,
        page_type: getPageType(),
        platform: 'web' as const,
        position: this.data.buttonPosition,
        previous_page_path: getPreviousPagePath(),
        previous_page_position: getPreviousPagePosition(),
        previous_page_type: getPreviousPageType(),
        product_brand: this.data.product.brand,
        product_id: this.getProductId(),
        product_name: this.getProductName(),
        product_sub_brand: this.data.product.sub_brand,
        product_view_price:
          this.data.product.price.sale ??
          this.data.product.price.regular ??
          undefined,
        retailer_click_price: this.getPrice(),
        retailer_id: this.data.affiliateId,
        retailer_name: this.getAffiliateName(),
        source_medium: getSourceAndMedium(),
        stock_status: this.data.product.stock_status,
        style_code: this.data.product.style_code
      },
      type: 'retailer_click'
    });
  }

  private getPrice() {
    return this.data.price ?? 0;
  }

  private getAffiliateName() {
    return this.data.affiliateName;
  }

  private getProductId() {
    return this.data.product.product_id;
  }

  private getProductName() {
    return this.data.product.name ?? '';
  }

  private getCurrencey() {
    return this.data.currency;
  }
}

export default AffiliateBuyNowEvent;
